import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  isCourseDescriptionEmpty,
  prepareCourseDescriptionForEditor,
  sanitizeCourseDescriptionHtml,
} from '../utils/courseDescriptionHtml';
import './CourseDescriptionEditor.css';

const TOOLBAR_ACTIONS = [
  { id: 'bold', label: 'Negrita', command: 'bold', customFormat: 'strong' },
  { id: 'h2', label: 'Título H2', command: 'formatBlock', value: 'h2', customFormat: 'h2' },
  { id: 'h3', label: 'Subtítulo H3', command: 'formatBlock', value: 'h3', customFormat: 'h3' },
  { id: 'ul', label: 'Lista con viñetas', command: 'insertUnorderedList' },
  { id: 'ol', label: 'Lista numerada', command: 'insertOrderedList' },
  { id: 'p', label: 'Párrafo', command: 'formatBlock', value: 'p' },
];

const BLOCK_TAGS = new Set(['P', 'H2', 'H3']);

function getEditorRange(editor) {
  const selection = globalThis.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) {
    return null;
  }
  return range;
}

function restoreSelection(range) {
  const selection = globalThis.getSelection();
  if (!selection) {
    return;
  }
  selection.removeAllRanges();
  selection.addRange(range);
}

function findEditableBlock(node, editor) {
  let current = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (current && current !== editor) {
    if (BLOCK_TAGS.has(current.tagName)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function findAncestorElement(node, editor, predicate) {
  let current = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (current && current !== editor) {
    if (predicate(current)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function unwrapElement(element) {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function applyStrongFormat(editor) {
  const range = getEditorRange(editor);
  if (!range || range.collapsed) {
    document.execCommand('bold', false);
    return;
  }

  const strongAncestor = findAncestorElement(range.commonAncestorContainer, editor, (element) => element.tagName === 'STRONG' || element.tagName === 'B');
  if (strongAncestor) {
    unwrapElement(strongAncestor);
    return;
  }

  const strong = document.createElement('strong');
  strong.appendChild(range.extractContents());
  range.insertNode(strong);

  const nextRange = document.createRange();
  nextRange.selectNodeContents(strong);
  restoreSelection(nextRange);
}

function applyBlockFormat(editor, tagName) {
  const range = getEditorRange(editor);
  const normalizedTagName = tagName.toLowerCase();
  const targetTagName = normalizedTagName === 'h2' || normalizedTagName === 'h3' ? normalizedTagName : 'p';
  if (!range) {
    document.execCommand('formatBlock', false, targetTagName);
    return;
  }

  const block = findEditableBlock(range.startContainer, editor);
  if (block) {
    const nextTagName = block.tagName.toLowerCase() === targetTagName ? 'p' : targetTagName;
    const replacement = document.createElement(nextTagName);
    replacement.innerHTML = block.innerHTML;
    block.replaceWith(replacement);

    const nextRange = document.createRange();
    nextRange.selectNodeContents(replacement);
    restoreSelection(nextRange);
    return;
  }

  if (range.collapsed) {
    document.execCommand('formatBlock', false, targetTagName);
    return;
  }

  const wrapper = document.createElement(targetTagName);
  wrapper.appendChild(range.extractContents());
  range.insertNode(wrapper);

  const nextRange = document.createRange();
  nextRange.selectNodeContents(wrapper);
  restoreSelection(nextRange);
}

function CourseDescriptionEditor({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  className,
}) {
  const editorRef = useRef(null);
  const lastEmittedValueRef = useRef('');

  const emitChange = useCallback(() => {
    const rawHtml = editorRef.current?.innerHTML ?? '';
    const sanitized = sanitizeCourseDescriptionHtml(rawHtml);
    lastEmittedValueRef.current = sanitized;
    onChange(sanitized);
  }, [onChange]);

  const runCommand = useCallback((action) => {
    if (disabled) {
      return;
    }
    const editor = editorRef.current;
    editor?.focus();
    if (!editor) {
      return;
    }
    if (action.customFormat === 'strong') {
      applyStrongFormat(editor);
    } else if (action.customFormat === 'h2' || action.customFormat === 'h3') {
      applyBlockFormat(editor, action.customFormat);
    } else {
      const blockValue = action.command === 'formatBlock' && action.value
        ? action.value
        : action.value ?? null;
      document.execCommand(action.command, false, blockValue);
    }
    emitChange();
  }, [disabled, emitChange]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const prepared = prepareCourseDescriptionForEditor(value);
    if (prepared !== lastEmittedValueRef.current && prepared !== editor.innerHTML) {
      editor.innerHTML = prepared;
      lastEmittedValueRef.current = prepared;
    }
  }, [value]);

  const handlePaste = (event) => {
    event.preventDefault();
    const plainText = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, plainText);
    emitChange();
  };

  const isEmpty = isCourseDescriptionEmpty(value);
  const surfaceClassName = [
    'course-description-editor__surface',
    className,
    isEmpty && required ? 'course-description-editor__surface--required-empty' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="course-description-editor">
      <div className="course-description-editor__toolbar" role="toolbar" aria-label="Formato de la descripción">
        {TOOLBAR_ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            className="course-description-editor__toolbar-btn"
            disabled={disabled}
            title={action.label}
            aria-label={action.label}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => runCommand(action)}
          >
            {action.label}
          </button>
        ))}
      </div>
      <div
        id={id}
        ref={editorRef}
        className={surfaceClassName}
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        aria-required={required ? 'true' : 'false'}
        aria-label="Descripción del curso"
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
      />
    </div>
  );
}

CourseDescriptionEditor.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
};

CourseDescriptionEditor.defaultProps = {
  id: undefined,
  value: '',
  placeholder: '',
  disabled: false,
  required: false,
  className: '',
};

export default CourseDescriptionEditor;
